import { useContext } from "react";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IOrder_ItemUI from "../../../models/IOrder_ItemUI";
import UserService from "../../../services/UserService";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UserTypesEnum from "../../../models/UserTypesEnum";
import "./LineItemQtyFulfillmentDetail.scss";

type Props = {
    row: IOrder_ItemUI;
};

const ALLOWED_USER_TYPES = [
    UserTypesEnum.ADMIN,
    UserTypesEnum.MANAGEMENT,
    UserTypesEnum.INVENTORY_MGMT,
    UserTypesEnum.ORDER_MGMT,
    UserTypesEnum.SHIP_MGMT,
];

type QtyField = {
    key: string;
    label: string;
    value: number;
    segmentClass?: string;
};

export const LineItemQtyFulfillmentDetail = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const showInventoryDetails = UserService.showInventoryDetails(supplierModel, userModel);

    if (!showInventoryDetails || !userModel.isSupplierUser) {
        return null;
    }

    const userTypeId = userModel.supplierUserToken?.user_type_id;
    if (!userTypeId || !ALLOWED_USER_TYPES.includes(userTypeId)) {
        return null;
    }

    if (!props.row.Catalog_Item?.has_inventory) {
        return null;
    }

    const qty = Number(props.row.qty) || 0;
    const qtyShipped = Number(props.row.qty_shipped) || 0;
    const qtyPicked = Number(props.row.qty_picked) || 0;
    const qtyReserved = Number(props.row.qty_reserved) || 0;
    const qtyPending = Number(props.row.qty_pending) || 0;
    const qtyOnRma = Number(props.row.qty_on_rma) || 0;
    const qtyReturned = Number(props.row.qty_returned) || 0;

    const fields: QtyField[] = [
        { key: "shipped", label: "shipped", value: qtyShipped, segmentClass: "segment-shipped" },
        { key: "picked", label: "picked", value: qtyPicked, segmentClass: "segment-picked" },
        { key: "reserved", label: "reserved", value: qtyReserved, segmentClass: "segment-reserved" },
        { key: "pending", label: "qty_pending", value: qtyPending },
        { key: "rma", label: "qty_on_rma", value: qtyOnRma },
        { key: "returned", label: "returned", value: qtyReturned },
    ];

    const visibleFields = fields.filter((f) => f.value > 0);

    if (visibleFields.length === 0) {
        return null;
    }

    const segments = fields.filter((f) => f.segmentClass && f.value > 0);
    const segmentTotal = segments.reduce((sum, s) => sum + s.value, 0);
    const barTotal = qty > 0 ? qty : segmentTotal;

    return (
        <div className="LineItemQtyFulfillmentDetail mt-1">
            <div className="d-flex justify-content-end flex-wrap text-small gap-2">
                {visibleFields.map((f) => (
                    <span key={f.key} className={"no-wrap " + (f.segmentClass ? "" : "text-muted")}>
                        <OwLabel title={f.label} postText={":"} className="me-1" />
                        {f.value}
                    </span>
                ))}
            </div>
            {barTotal > 0 && segments.length > 0 ? (
                <div className="fill-bar-track">
                    {segments.map((s) => (
                        <div
                            key={s.key}
                            className={`fill-bar-segment ${s.segmentClass}`}
                            style={{ width: `${Math.min(100, (s.value / barTotal) * 100)}%` }}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
};
